// src/components/PractitionerWeeklySchedule.tsx
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils"; // From shadcn setup
import { PractitionerScheduleEntry } from '@prisma/client'; // Or define manually
import { format } from 'date-fns';
import { toZonedTime } from "date-fns-tz";
import { de } from 'date-fns/locale';
import dayjs from 'dayjs';
import { ArrowLeft, ArrowRight, CalendarIcon, PlusCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { datetime, RRule, RRuleSet } from 'rrule';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { formatAvailabilityHours, getDailyBreakDeductionMinutes, getDeduplicatedDurationMinutes } from '~/lib/availability-duration';
import LocalDateTime from '~/lib/localdatetime';
import { useUserStore } from '~/stores/user-store';
import { PractitionerScheduleEntryType } from '~/types/server-types/PractitionerScheduleEntryType';
import { ScheduleEntryForm } from './ScheduleEntryForm'; // Import the form

interface PractitionerWeeklyScheduleProps {
    practitionerId: number;
    // Optional props for work hours, can be dynamic later
    workHourStart?: number; // e.g., 8 (for 8 AM)
    workHourEnd?: number;   // e.g., 18 (for 6 PM)
    slotDurationMinutes?: number; // e.g., 60 or 30
}

interface ProcessedEvent {
    id: string; // Original entry ID
    start: LocalDateTime;
    end: LocalDateTime;
    type: PractitionerScheduleEntryType;
    description: string | null;
    isRecurring: boolean;
    originalEntry: PractitionerScheduleEntry; // Keep ref to original
}

const DAY_FORMAT = 'EEEEEE d MMM';
const TIME_FORMAT = 'HH:mm';

const PractitionerWeeklySchedule: React.FC<PractitionerWeeklyScheduleProps> = ({
    practitionerId,
    workHourStart = 7,
    workHourEnd = 21,
    slotDurationMinutes = 15,
}) => {
    const utils = trpc.useUtils();

    const { timezone: userTimezone } = useUserStore(
        useShallow((state) => ({ timezone: state.user?.timezone }))
    );

    const [searchParams] = useSearchParams();

    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const weekParam = searchParams.get('week');
        if (weekParam) {
            const parsed = dayjs(weekParam).startOf('isoWeek');
            if (parsed.isValid()) {
                return LocalDateTime.fromUTCDate(parsed.utc().toDate(), 'Europe/Berlin').setHours(0).setMinutes(0);
            }
        }
        const baseDate = dayjs().weekday(0).utc().toDate();
        return LocalDateTime.fromUTCDate(baseDate, 'Europe/Berlin').setHours(0).setMinutes(0);
    });

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState<PractitionerScheduleEntry | null>(null);
    const [selectedSlotTime, setSelectedSlotTime] = useState<{ start: LocalDateTime, end: LocalDateTime } | null>(null);

    // Calculate week boundaries based on currentWeekStart and timezone
    const { weekStart, weekEnd, daysOfWeek } = useMemo(() => {
        // Ensure start/end cover the *entire* week for querying backend overlap
        // Use startOf('day') and endOf('day') for robustness with backend query
        const start = currentWeekStart;

        const startDayJs = dayjs(start.toUTC()).startOf('isoWeek').startOf('day').set('hour', 0);
        const endDayJs = startDayJs.endOf('isoWeek').endOf('day');

        const end = LocalDateTime.fromUTCDate(endDayJs.utc().toDate(), 'Europe/Berlin');

        const days = Array.from({ length: 7 }).map((_, i) => startDayJs.add(i, 'day').tz(userTimezone || 'Europe/Berlin')).map((dayDayJs) => LocalDateTime.fromUTCDate(dayDayJs.utc().toDate(), 'Europe/Berlin'));

        return { weekStart: start, weekEnd: end, daysOfWeek: days };
    }, [currentWeekStart, userTimezone]);

    const practitionerQuery = trpc.practitioners.getOne.useQuery({
        id: practitionerId,
    });

    const practitionerStores = useMemo(() => {
        if (practitionerQuery.data) {
            return practitionerQuery.data.stores;
        } else {
            return [];
        }
    }, [practitionerQuery]);

    const [selectedStore, setSelectedStore] = useState<typeof practitionerStores[0] | undefined>(undefined);
    useEffect(() => {
        if (practitionerStores.length > 0 && !selectedStore) {
            setSelectedStore(practitionerStores[0]);
        }
    }, [practitionerStores]);

    // *** MODIFIED useQuery Hook ***
    const { data: scheduleEntries, isLoading, error: fetchError } = trpc.practitioners.getScheduleEntries.useQuery(
        {
            practitionerId,
            storeId: selectedStore?.id || 0,
            // Pass week boundaries as full ISO timestamps (UTC) to include time component
            // Using just date strings (yyyy-MM-dd) caused Sunday entries to be excluded
            // because new Date("2026-01-25") becomes midnight UTC, excluding entries starting later that day
            startTime: weekStart.toUTC().toISOString(),
            endTime: weekEnd.toUTC().toISOString(),
        },
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: true,
            enabled: !!practitionerId && !!weekStart && !!weekEnd, // Ensure dependencies are ready
            // The `select` function is still useful to ensure date objects on client
            select: (data) => data?.map(entry => ({
                ...entry,
                // Parse ISO strings back to Date objects (or dayjs if preferred)
                startTime: new Date(entry.startTime),
                endTime: new Date(entry.endTime),
                startTimeLocal: LocalDateTime.deserialize(entry.startTimeLocal),
                endTimeLocal: LocalDateTime.deserialize(entry.endTimeLocal),
                recurrenceEndDate: entry.recurrenceEndDate ? new Date(entry.recurrenceEndDate) : null,
                // Ensure correct type mapping if prisma client types aren't perfect over tRPC
                type: entry.type as PractitionerScheduleEntryType,
                isRecurring: !!entry.isRecurring, // Ensure boolean
            })) || [],
        }
    );

    // Process fetched entries to expand recurring events for the current week
    const processedEvents = useMemo((): ProcessedEvent[] => {
        if (!scheduleEntries) return [];

        // Convert query range to UTC for rrule.between
        const weekStartUtc = weekStart.toUTC();
        // Use the *precise* end for rrule (end of the last day of the week)
        const weekEndUtc = weekEnd.toUTC();

        const events: ProcessedEvent[] = [];

        scheduleEntries.forEach((entry) => {
            // Define start and end for rrule processing (use UTC to avoid DST issues)
            const entryStartLocal = entry.startTimeLocal;
            const entryEndLocal = entry.endTimeLocal;

            const entryStartDate = entryStartLocal.toTimezonedDate();
            const entryStartDatetime = datetime(entryStartDate.getUTCFullYear(), entryStartDate.getUTCMonth(), entryStartDate.getUTCDate(), entryStartDate.getUTCHours(), entryStartDate.getUTCMinutes());

            // console.log("FROM entry start date", entryStartLocal);
            // console.log("Timezoned entry start date", entryStartDate);
            const entryEndDate = entryEndLocal.toTimezonedDate();
            const durationMs = entryEndDate.getTime() - entryStartDate.getTime();
            const durationMinutes = durationMs / 60000;

            if (entry.isRecurring && entry.recurrenceRule) {
                try {
                    // const rrule = rrulestr(entry.recurrenceRule, { dtstart: entryStartDatetime, tzid: entryStartLocal.getTimezone() });
                    // const ruleSet = new RRuleSet();
                    // ruleSet.rrule(rrule);

                    const ruleOptions = RRule.parseString(entry.recurrenceRule);
                    ruleOptions.dtstart = entry.startTimeLocal.toUTC();

                    if (entry.recurrenceEndDate) {
                        const untilDate = new Date(entry.recurrenceEndDate);
                        untilDate.setHours(23, 59, 59, 999);
                        ruleOptions.until = untilDate;
                    }

                    const rrule = new RRule(ruleOptions);
                    const ruleSet = new RRuleSet();
                    ruleSet.rrule(rrule);

                    if (entry.recurrenceRuleExceptions) {
                        console.log("entry.recurrenceRuleExceptions");
                        console.log(entry.recurrenceRuleExceptions);

                        const entryStartUTC = entry.startTimeLocal.toUTC();

                        // Parse the exceptions using the new format
                        const parsedExceptions = entry.recurrenceRuleExceptions
                            .split(",")
                            .map((s) => s.trim())
                            .filter((s) => s.length > 0)
                            .map((exceptionEntry) => {
                                const [datePart, objectPart] = exceptionEntry.split('_');
                                const date = new Date(datePart);

                                if (isNaN(date.getTime())) {
                                    console.warn(`Invalid exception date: ${datePart}`);
                                    return null;
                                }

                                const startTime = new Date(entryStartUTC);
                                date.setUTCHours(startTime.getUTCHours());
                                date.setUTCMinutes(startTime.getUTCMinutes());
                                date.setUTCSeconds(startTime.getUTCSeconds());
                                date.setUTCMilliseconds(startTime.getUTCMilliseconds());

                                let objectIds: number[] | undefined;
                                if (objectPart) {
                                    objectIds = objectPart
                                        .split('+')
                                        .map((id) => parseInt(id))
                                        .filter((id) => !isNaN(id));
                                }

                                return { date, objectIds };
                            })
                            .filter((exception) => exception !== null);

                        // Filter exceptions that apply to this practitioner
                        const applicableExceptions = parsedExceptions.filter((exception) => {
                            if (!exception.objectIds || exception.objectIds.length === 0) {
                                return true;
                            }
                            return exception.objectIds.includes(entry.practitionerId);
                        });
            
                        applicableExceptions.forEach((exception) => ruleSet.exdate(exception.date));
                    }

                    // Get occurrences within the date range
                    const queryFromDate = weekStartUtc;
                    const queryToDate = weekEndUtc;
                    const lookbackStart = entry.startTimeLocal.toUTC() > queryFromDate
                        ? entry.startTimeLocal.toUTC()
                        : new Date(queryFromDate.getTime() - (durationMinutes * 60 * 1000));

                    const occurrences = ruleSet.between(lookbackStart, queryToDate, true);

                    const timezone = weekStart.getTimezone();
                    const originalLocalTime = entry.startTimeLocal.getDisplayTime(); // e.g., "10:00"
                    const [originalHour, originalMinute] = originalLocalTime.split(':').map(Number);

                    // const occurrences = ruleSet.between(weekStartUtc, weekEndUtc, true).map((d) => new Date(
                    //     d.getUTCFullYear(),
                    //     d.getUTCMonth(),
                    //     d.getUTCDate(),
                    //     d.getUTCHours(),
                    //     d.getUTCMinutes(),
                    // )) // true includes start date
                    // console.log("occurrences for", entry.practitionerId);
                    // console.log(occurrences);

                    occurrences.forEach((occStartUTC) => {
                        const occDate = new Date(occStartUTC);

                        const year = occDate.getUTCFullYear();
                        const month = occDate.getUTCMonth();
                        const day = occDate.getUTCDate();

                        const occStart = LocalDateTime.fromLocalDate(
                            new Date(year, month, day), 
                            timezone, 
                            originalHour, 
                            originalMinute
                        );

                        const occEnd = occStart.addMinutes(durationMinutes);

                        const effectiveStart = occStart.greaterThan(weekStart) ? occStart : weekStart;
                        const effectiveEnd = occEnd.lessThan(weekEnd) ? occEnd : weekEnd;

                        // No need for the extra boundary check here, rule.between handled it
                        events.push({
                            id: entry.id, // Use original entry ID
                            start: effectiveStart,
                            end: effectiveEnd,
                            type: entry.type,
                            description: entry.description,
                            isRecurring: true,
                            // Pass the original raw entry for editing purposes
                            originalEntry: entry as unknown as PractitionerScheduleEntry, // Cast needed if select changed type slightly
                        });
                    });
                } catch (e) {
                    console.error(`Error parsing RRULE for entry ${entry.id}:`, entry.recurrenceRule, e);
                }
            } else {
                // Single Entry - Already filtered by backend overlap, just convert to display timezone
                const singleStart = entryStartLocal;
                const singleEnd = entryEndLocal;

                events.push({
                    id: entry.id,
                    start: singleStart,
                    end: singleEnd,
                    type: entry.type,
                    description: entry.description,
                    isRecurring: false,
                    originalEntry: entry as unknown as PractitionerScheduleEntry, // Cast needed
                });
            }
        });


        return events.sort((a, b) => a.start.valueOf() - b.start.valueOf());

    }, [scheduleEntries, weekStart, weekEnd, userTimezone]); // Dependencies are correct

    const weeklyAvailabilityMinutes = useMemo(() => {
        const weekStartTime = weekStart.toUTC().getTime();
        const weekEndTime = weekEnd.toUTC().getTime();

        const intervalsByDay = new Map<string, { start: number; end: number }[]>();
        processedEvents
            .filter(event => event.type === PractitionerScheduleEntryType.AVAILABLE)
            .forEach(event => {
                const start = Math.max(event.start.toUTC().getTime(), weekStartTime);
                const end = Math.min(event.end.toUTC().getTime(), weekEndTime);
                if (end <= start) return;

                const dayKey = event.start.getDisplayDate();
                const dayIntervals = intervalsByDay.get(dayKey) ?? [];
                dayIntervals.push({ start, end });
                intervalsByDay.set(dayKey, dayIntervals);
            });

        let total = 0;
        for (const dayIntervals of intervalsByDay.values()) {
            const dailyMinutes = getDeduplicatedDurationMinutes(dayIntervals);
            total += Math.max(dailyMinutes - getDailyBreakDeductionMinutes(dailyMinutes), 0);
        }

        return total;
    }, [processedEvents, weekStart, weekEnd]);

    // Generate time slots for the day view
    const timeSlots = useMemo(() => {
        const slots = [];
        let currentTime = weekStart.clone().setHours(workHourStart).setMinutes(0);
        const dayEnd = weekStart.clone().setHours(workHourEnd).setMinutes(0);

        while (currentTime.lessThan(dayEnd)) {
            slots.push(currentTime);
            currentTime = currentTime.addMinutes(slotDurationMinutes);
        }
        return slots;
    }, [weekStart, workHourStart, workHourEnd, slotDurationMinutes]);


    console.log(timeSlots);
    // --- Interaction Handlers ---

    const handlePrevWeek = () => {
        // Subtract exactly one week (7 days)
        console.log("subtracting 1 week from: " + currentWeekStart.toUTC().toISOString())
        setCurrentWeekStart(currentWeekStart.addDays(-7));
    };

    const handleNextWeek = () => {
        // Add exactly one week (7 days)
        console.log("adding 1 week to: " + currentWeekStart.toUTC().toISOString())
        setCurrentWeekStart(currentWeekStart.addDays(7));
    };

    const handleTodayClick = () => {
        const today = LocalDateTime.now("Europe/Berlin");
        const todayWeekStart = today.setDayOfWeek("monday").setHours(0).setMinutes(0);
        setCurrentWeekStart(todayWeekStart);
    };

    const handleDateChange = useCallback((newDate: Date | undefined) => {
        if (!newDate) {
            return;
        }
        const newDateStr = format(newDate, "yyyy-MM-dd");
        const localNewDate = new LocalDateTime(`${newDateStr} 00:00`, "Europe/Berlin");
        const newWeekStart = localNewDate.setDayOfWeek("monday").setHours(0).setMinutes(0);
        setCurrentWeekStart(newWeekStart);
    }, []);

    // Convert week dates to Date array for calendar selection display
    const selectedDates: Date[] = useMemo(() => {
        const dateArray: Date[] = [];
        let currentDate = weekStart.clone();
        while (currentDate <= weekEnd) {
            dateArray.push(toZonedTime(new Date(currentDate.getDisplayDate()), "Europe/Berlin"));
            currentDate = currentDate.addDays(1);
        }
        return dateArray;
    }, [weekStart, weekEnd]);

    const handleOpenAddForm = (slotStart?: LocalDateTime, day?: LocalDateTime) => {
        setEditingEntry(null); // Ensure we are adding, not editing
        if (slotStart && day) {
            const start = day.clone().setHours(slotStart.getHours()).setMinutes(slotStart.getMinutes());
            const end = start.clone().addMinutes(slotDurationMinutes);
            setSelectedSlotTime({ start, end });
        } else {
            setSelectedSlotTime(null); // Clear selection if general add button used
        }
        setIsFormOpen(true);
    };

    const handleOpenEditForm = (event: ProcessedEvent) => {
        // Pass the *original* entry data for editing, as it contains the RRULE etc.
        setEditingEntry(event.originalEntry);
        // When editing an existing entry, we want to pre-select the slot time from the event
        setSelectedSlotTime({
            start: event.start,
            end: event.end,
        });
        setIsFormOpen(true);
    };

    // --- Rendering ---

    if (fetchError) {
        return <Card className="border-destructive"><CardHeader><CardTitle>Error Loading Schedule</CardTitle></CardHeader><CardContent>{fetchError.message}</CardContent></Card>;
    }

    return (
        <TooltipProvider delayDuration={300}>
            <Card className="w-full">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-0">
                    <CardTitle className="w-full text-lg font-medium">
                        <div>Practitioner Schedule ({userTimezone || 'Europe/Berlin'})</div>
                        <div className="mt-1 text-sm font-normal text-muted-foreground">
                            Wochenverfügbarkeit: {formatAvailabilityHours(weeklyAvailabilityMinutes)} Std.
                        </div>
                    </CardTitle>
                    <div className="w-full flex justify-end items-center space-x-2">
                        <div className='flex'>
                            <Select value={selectedStore?.id.toString() || ''} onValueChange={(val) => setSelectedStore(practitionerStores.find((s) => s.id.toString() === val))}>
                                <SelectTrigger className='w-full'>
                                    <SelectValue placeholder='Location' />
                                </SelectTrigger>
                                <SelectContent>
                                    {practitionerStores && practitionerStores.map((option) => (
                                        <SelectItem key={option.id.toString()} value={option.id.toString()}>
                                            {option.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='flex flex-row items-center gap-2'>
                            {/* Today Button */}
                            <Button variant='outline' onClick={handleTodayClick} className='w-24' disabled={isLoading}>
                                Heute
                            </Button>

                            {/* Navigation with Date Picker */}
                            <div className={cn('grid gap-2')}>
                                <div className='flex items-center'>
                                    <Button variant='outline' size='icon' onClick={handlePrevWeek} className='mr-2' disabled={isLoading}>
                                        <ArrowLeft className='h-4 w-4' />
                                    </Button>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id='date'
                                                variant={'outline'}
                                                className={cn('w-[300px] justify-start text-left font-normal', !weekStart && 'text-muted-foreground')}
                                                disabled={isLoading}
                                            >
                                                <CalendarIcon className='mr-2 h-4 w-4' />
                                                {weekStart ? (
                                                    <>
                                                        {format(toZonedTime(new Date(weekStart.getDisplayDate()), "Europe/Berlin"), 'dd.MM.yyyy', { locale: de })}
                                                        {' - '}
                                                        {format(toZonedTime(new Date(weekEnd.getDisplayDate()), "Europe/Berlin"), 'dd.MM.yyyy', { locale: de })}
                                                    </>
                                                ) : (
                                                    <span>Pick a date</span>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className='w-auto p-0' align='start'>
                                            <Calendar
                                                mode='multiple'
                                                locale={de}
                                                defaultMonth={new Date(weekStart.getDisplayDateTime())}
                                                selected={selectedDates}
                                                onDayClick={handleDateChange}
                                                numberOfMonths={2}
                                                weekStartsOn={1}
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Button variant='outline' size='icon' onClick={handleNextWeek} className='ml-2' disabled={isLoading}>
                                        <ArrowRight className='h-4 w-4' />
                                    </Button>
                                </div>
                            </div>

                            <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                                <DialogTrigger asChild>
                                    <Button size="sm" onClick={() => handleOpenAddForm()}>
                                        <PlusCircle className="mr-2 h-4 w-4" /> Add Entry
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle>{editingEntry ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</DialogTitle>
                                    </DialogHeader>
                                    {selectedStore && (
                                        <ScheduleEntryForm
                                            key={editingEntry?.id || 'new'} // Force re-render on edit vs add
                                            dataObjectId={practitionerId}
                                            dataObjectType='practitioner'
                                            storeId={selectedStore.id}
                                            userTimezone={userTimezone || 'Europe/Berlin'}
                                            scheduleEntryId={editingEntry?.id ?? undefined}
                                            selectedSlot={selectedSlotTime ?? undefined}
                                            onSuccess={() => {
                                                setIsFormOpen(false);
                                                setEditingEntry(null);
                                                setSelectedSlotTime(null);
                                            }}
                                            onCancel={() => {
                                                setIsFormOpen(false);
                                                setEditingEntry(null);
                                                setSelectedSlotTime(null);
                                            }}
                                        />
                                    )}
                                </DialogContent>
                            </Dialog>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {/* Loading Skeleton or indicator */}
                    {isLoading && (
                        <div className="p-6 text-center text-muted-foreground">Loading schedule...</div>
                    )}
                    {!isLoading && (
                        <div className="grid grid-cols-[auto_repeat(7,1fr)] border-t border-border">
                            {/* Time Column */}
                            <div className="border-r border-border bg-muted/30 sticky left-0 z-20"> {/* Increased z-index */}
                                <div className="h-10 border-b border-border"></div> {/* Spacer for day header */}
                                {timeSlots.map((slot, idx) => (
                                    <div key={slot.toUTC().toISOString()}
                                        className={cn(
                                            "flex items-center justify-center text-xs border-b border-border text-muted-foreground"
                                            // Removed h-8/h-12, height is now solely from style
                                        )}
                                        style={{ height: `${slotDurationMinutes < 60 ? 1 : 1.5}rem` }}
                                    >
                                        {idx % (60 / slotDurationMinutes / 2) === 0 && slot.format(TIME_FORMAT)} {/* Show time label e.g. every hour if 15/30min slots */}
                                    </div>
                                ))}
                            </div>

                            {/* Day Columns */}
                            {daysOfWeek.map((day) => {
                                const actualSlotHeightRem = slotDurationMinutes < 60 ? 1 : 1.5;

                                // Filter and sort events for the current day
                                const dayEvents = processedEvents.filter(event =>
                                    // Event overlaps with the current day
                                    event.start.isSameDay(day) || event.end.isSameDay(day)
                                ).sort((a, b) => { // Sort to help with rendering order if needed, though z-index is primary
                                    if (a.type === PractitionerScheduleEntryType.BLOCKED && b.type !== PractitionerScheduleEntryType.BLOCKED) return 1;
                                    if (a.type !== PractitionerScheduleEntryType.BLOCKED && b.type === PractitionerScheduleEntryType.BLOCKED) return -1;
                                    return a.start.valueOf() - b.start.valueOf();
                                });

                                return (
                                    <div key={day.toUTC().toISOString()} className="border-r border-border last:border-r-0">
                                        {/* Day Header */}
                                        <div className="h-10 border-b border-border text-center flex items-center justify-center text-sm font-medium sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-20"> {/* Increased z-index */}
                                            {day.format(DAY_FORMAT)}
                                        </div>

                                        {/* Container for slots and events (relative positioning context) */}
                                        <div className="relative">
                                            {/* Render Empty Time Slots (for background grid and click-to-add) */}
                                            {timeSlots.map((slot, slotIndex) => (
                                                <div
                                                    key={`empty-${day.valueOf()}-${slotIndex}`}
                                                    className="border-b border-border cursor-pointer hover:bg-muted/50 transition-colors"
                                                    style={{ height: `${actualSlotHeightRem}rem` }}
                                                    onClick={() => handleOpenAddForm(slot, day)}
                                                    role="button"
                                                    aria-label={`Add entry for ${day.format(DAY_FORMAT)} at ${slot.format(TIME_FORMAT)}`}
                                                >
                                                    {/* Can be truly empty or have a faint line for 15/30 min marks if desired */}
                                                </div>
                                            ))}

                                            {/* Render Processed Events for the Day */}
                                            {dayEvents.map((event) => {
                                                const dayWithWorkdayStart = day.clone().setHours(workHourStart).setMinutes(0);
                                                const dayWithWorkdayEnd = day.clone().setHours(workHourEnd).setMinutes(59);

                                                // Calculate event's visual start and end time, clamped to the current day and work hours
                                                // Convert dates to timestamps for comparison
                                                const dayStart = day.getStartOfDay().toUTC().getTime();
                                                const dayEnd = day.getEndOfDay().toUTC().getTime();
                                                const workdayStart = dayWithWorkdayStart.toUTC().getTime();
                                                const workdayEnd = dayWithWorkdayEnd.toUTC().getTime();
                                                const eventStart = event.start.toUTC().getTime();
                                                const eventEnd = event.end.toUTC().getTime();

                                                // Calculate clamped start and end times
                                                let eventStartOnDay = Math.max(dayStart, Math.max(workdayStart, eventStart));
                                                let eventEndOnDay = Math.min(dayEnd, Math.min(workdayEnd, eventEnd));

                                                // Skip rendering if the event segment is outside work hours or has no duration within them
                                                if (eventStartOnDay >= eventEndOnDay ||
                                                    eventStartOnDay >= workdayEnd ||
                                                    eventEndOnDay <= workdayStart) {
                                                    return null;
                                                }

                                                // Calculate durations in minutes
                                                const minutesFromWorkdayStartToEventStart = Math.floor((eventStartOnDay - workdayStart) / (1000 * 60));
                                                const eventDurationMinutesOnDay = Math.floor((eventEndOnDay - eventStartOnDay) / (1000 * 60));

                                                if (eventDurationMinutesOnDay <= 0) return null;

                                                const topOffset = (minutesFromWorkdayStartToEventStart / slotDurationMinutes) * actualSlotHeightRem;
                                                const height = (eventDurationMinutesOnDay / slotDurationMinutes) * actualSlotHeightRem;

                                                const bgColor = event.type === PractitionerScheduleEntryType.AVAILABLE
                                                    ? 'bg-green-100 hover:bg-green-200'
                                                    : 'bg-red-200 hover:bg-red-300'; // Changed Blocker to red shades

                                                const eventTextColor = event.type === PractitionerScheduleEntryType.AVAILABLE
                                                    ? 'text-green-800'
                                                    : 'text-red-800'; // Text color matching background theme

                                                const zIndex = event.type === PractitionerScheduleEntryType.BLOCKED ? 15 : 10; // Blockers on top

                                                return (
                                                    <Tooltip key={`${event.id}-${event.start.toUTC().toISOString()}`} /* Unique key for recurring instances */>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                className={cn(
                                                                    "absolute w-[calc(100%-4px)] left-[2px] right-[2px]", // Position absolutely, slight horizontal margin for clear separation
                                                                    bgColor,
                                                                    "flex p-1 overflow-hidden rounded shadow-sm cursor-pointer border" // Added generic border
                                                                )}
                                                                style={{
                                                                    top: `${topOffset}rem`,
                                                                    height: `${height}rem`,
                                                                    zIndex: zIndex,
                                                                    borderColor: event.type === PractitionerScheduleEntryType.AVAILABLE ? 'rgb(22 101 52 / 0.4)' : 'rgb(153 27 27 / 0.4)', // theme matching border
                                                                }}
                                                                onClick={() => handleOpenEditForm(event)}
                                                                role="button"
                                                            >
                                                                <span className={cn('text-xs leading-tight px-1', eventTextColor)}>
                                                                    [{event.start.format(TIME_FORMAT)} bis {event.end.format(TIME_FORMAT)}] - {event.description || event.type.toString()}
                                                                </span>
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="flex flex-col text-black bg-background border border-border shadow-lg rounded-md p-3 w-[400px]">
                                                            <p className="font-semibold">{event.type.toString()}</p>
                                                            <p className="text-sm text-muted-foreground">
                                                                {event.start.format(TIME_FORMAT)} - {event.end.format(TIME_FORMAT)}
                                                            </p>
                                                            {event.description && <p className="text-sm mt-1">{event.description}</p>}
                                                            {event.isRecurring && <p className="text-xs mt-1 italic">Recurring</p>}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </TooltipProvider>
    );
};

export default PractitionerWeeklySchedule